import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { AboutDialog } from '@/features/about/AboutDialog';
import * as Dialog from '@radix-ui/react-dialog';
import { useQueryClient } from '@tanstack/react-query';
import { useSession, useLogout, hasAnyPermission, type Session } from '@/shared/api/session';
import { onAuthStatus } from '@/shared/api/client';
import { UserAvatar } from '@/shared/ui';
import { BrandLogo, SYSTEM_NAME } from '@/shared/ui/brand';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { useAppConfig } from '@/app/config';
import s from './AppShell.module.css';

type Item = { to: string; label: string; anyOf?: string[] };
type Section = { title: string; items: Item[] };

/** Navegación conceptual §31.3, filtrada por permisos. Rutas de fases posteriores quedan declaradas para el árbol. */
const SECTIONS: Section[] = [
  {
    title: 'OPERACIÓN',
    items: [
      { to: '/', label: 'Dashboard' },
      { to: '/reportes', label: 'Dashboard gerencial', anyOf: ['reportes:ver'] },
      { to: '/solicitudes', label: 'Solicitudes de fondos', anyOf: ['fxr:solicitar'] },
      { to: '/rendiciones', label: 'Rendiciones por revisar', anyOf: ['rendicion:revisar_operacional'] },
      { to: '/mis-fondos', label: 'Mis fondos' },
      { to: '/gastos/nuevo', label: 'Nuevo gasto' },
      { to: '/mis-informes', label: 'Mis informes' },
      { to: '/mis-restituciones', label: 'Mis restituciones' },
      { to: '/cuenta', label: 'Mi cuenta corriente' },
    ],
  },
  {
    title: 'FINANZAS',
    items: [
      { to: '/finanzas/solicitudes', label: 'Solicitudes pendientes', anyOf: ['fxr:procesar'] },
      { to: '/finanzas/rendiciones', label: 'Rendiciones por revisar', anyOf: ['rendicion:revisar_financiera'] },
      { to: '/finanzas/reembolsos', label: 'Reembolsos', anyOf: ['reembolso:gestionar'] },
      { to: '/finanzas/restituciones', label: 'Restituciones', anyOf: ['restitucion:gestionar'] },
    ],
  },
  { title: 'GERENCIA', items: [{ to: '/gerencia/excepciones', label: 'Excepciones', anyOf: ['fxr:aprobar_excepcion'] }, { to: '/gerencia/rendiciones', label: 'Rendiciones (rectificar)', anyOf: ['rendicion:rectificar'] }] },
  {
    title: 'ADMINISTRACIÓN',
    items: [
      { to: '/admin/usuarios', label: 'Usuarios', anyOf: ['usuarios:administrar'] },
      { to: '/admin/categorias', label: 'Categorías', anyOf: ['categorias:administrar'] },
      { to: '/admin/viaticos', label: 'Viáticos', anyOf: ['viaticos:administrar'] },
      { to: '/admin/limites', label: 'Límites FXR', anyOf: ['limites:administrar'] },
      { to: '/admin/configuracion', label: 'Configuración', anyOf: ['config:administrar'] },
      { to: '/admin/configuracion/correo', label: 'Correo (SMTP)', anyOf: ['smtp:administrar'] },
      { to: '/admin/notificaciones', label: 'Notificaciones', anyOf: ['notificaciones:administrar'] },
      { to: '/admin/auditoria', label: 'Auditoría', anyOf: ['auditoria:leer'] },
      { to: '/admin/ia', label: 'IA (documentos)', anyOf: ['ia:administrar'] },
    ],
  },
];

function visibleSections(session: Session) {
  return SECTIONS.map((sec) => ({ ...sec, items: sec.items.filter((i) => !i.anyOf || hasAnyPermission(session, i.anyOf)) })).filter((sec) => sec.items.length > 0);
}

function Nav({ session, onNavigate }: { session: Session; onNavigate?: () => void }) {
  return (
    <nav aria-label="Navegación principal">
      {visibleSections(session).map((sec) => (
        <div key={sec.title}>
          <div className={s.section}>{sec.title}</div>
          <div className={s.nav}>
            {sec.items.map((i) => (
              <NavLink key={i.to} to={i.to} end={i.to === '/'} className={({ isActive }) => `${s.link} ${isActive ? s.active : ''}`} onClick={onNavigate}>
                {i.label}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

export function AppShell() {
  const { data: session } = useSession();
  const logout = useLogout();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const config = useAppConfig();
  const [drawer, setDrawer] = useState(false);
  const [about, setAbout] = useState(false);

  // 401 en cualquier llamada → la sesión caducó: limpiar y volver al login
  useEffect(() => {
    const off = onAuthStatus((st) => { if (st === 401) { qc.setQueryData(['auth', 'session'], null); navigate('/auth/login', { replace: true }); } });
    return () => { off(); };
  }, [navigate, qc]);

  if (!session) return null;
  const mobileItems: Item[] = [{ to: '/', label: 'Inicio' }, { to: '/mis-fondos', label: 'Fondos' }, { to: '/gastos/nuevo', label: '+ Gasto' }, { to: '/mis-informes', label: 'Informes' }, { to: '/notificaciones', label: 'Avisos' }];

  return (
    <div className={s.shell}>
      <aside className={s.sidebar}>
        <div className={s.logo} title={SYSTEM_NAME}><BrandLogo height={26} /><span className={s.brandSystem}>SGR<span className={s.version}>{config.version}</span></span></div>
        <Nav session={session} />
      </aside>
      <div className={s.main}>
        <header className={s.topbar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <Dialog.Root open={drawer} onOpenChange={setDrawer}>
              <Dialog.Trigger className={`${s.iconBtn} ${s.menuToggle}`} aria-label="Abrir menú">☰</Dialog.Trigger>
              <Dialog.Portal>
                <Dialog.Overlay className={s.drawer}>
                  <Dialog.Content className={s.drawerPanel} aria-describedby={undefined}>
                    <Dialog.Title className={s.logo}><BrandLogo height={24} /><span className={s.brandSystem}>SGR</span></Dialog.Title>
                    <Nav session={session} onNavigate={() => setDrawer(false)} />
                  </Dialog.Content>
                </Dialog.Overlay>
              </Dialog.Portal>
            </Dialog.Root>
            <strong style={{ fontSize: 'var(--fs-sm)' }}>{session.user.full_name}</strong>
          </div>
          <div className={s.userMenu}>
            <NotificationBell />
            <DropdownMenu.Root>
              <DropdownMenu.Trigger className={s.iconBtn} aria-label="Menú de usuario"><UserAvatar name={session.user.full_name} /></DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content className={s.menu} align="end" sideOffset={6}>
                  <div className={s.menuLabel}>{session.user.email}<br />{session.roles.join(', ')}</div>
                  <DropdownMenu.Item className={s.menuItem} onSelect={() => navigate('/cuenta/seguridad')}>Seguridad y MFA</DropdownMenu.Item>
                  <DropdownMenu.Item className={s.menuItem} onSelect={() => setAbout(true)}>Acerca de SGR</DropdownMenu.Item>
                  <DropdownMenu.Item className={s.menuItem} onSelect={() => logout.mutate(undefined, { onSettled: () => navigate('/auth/login', { replace: true }) })}>Cerrar sesión</DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </header>
        <main className={s.content}><Outlet /></main>
        <nav className={s.bottomBar} aria-label="Navegación móvil">
          {mobileItems.map((i) => (
            <NavLink key={i.to} to={i.to} end={i.to === '/'} className={({ isActive }) => `${s.bottomLink} ${i.to === '/gastos/nuevo' ? s.bottomPrimary : ''} ${isActive ? s.active : ''}`} aria-label={i.to === '/gastos/nuevo' ? 'Nuevo gasto' : undefined}>{i.label}</NavLink>
          ))}
        </nav>
      </div>
      <AboutDialog open={about} onOpenChange={setAbout} />
    </div>
  );
}
